import axios from "axios";

// ════════════════════════════════════════════════════════════
// Dashboard & Reports API
// ────────────────────────────────────────────────────────────
// There is NO dedicated /v1/dashboard endpoint.  Everything is
// composed from the real report, machine, schedule, production
// & flow endpoints.
// ════════════════════════════════════════════════════════════

// ── Helpers ──────────────────────────────────────────────────
const today = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

// ── Reports (real endpoints) ─────────────────────────────────
// GET /v1/reports/production
export const getProductionReport = async (params = {}) => {
  const { data } = await axios.get("reports/production", { params });
  return data;
};

// GET /v1/reports/job-completion
export const getJobCompletionReport = async (params = {}) => {
  const { data } = await axios.get("reports/job-completion", { params });
  return data;
};

// GET /v1/reports/wastage
export const getWastageReport = async (params = {}) => {
  const { data } = await axios.get("reports/wastage", { params });
  return data;
};

// GET /v1/reports/material-consumption
export const getMaterialConsumptionReport = async (params = {}) => {
  const { data } = await axios.get("reports/material-consumption", { params });
  return data;
};

// GET /v1/reports/order-pipeline
export const getOrderPipelineReport = async () => {
  const { data } = await axios.get("reports/order-pipeline");
  return data;
};

// GET /v1/reports/customer-sales
export const getCustomerSalesReport = async (params = {}) => {
  const { data } = await axios.get("reports/customer-sales", { params });
  return data;
};

// ── Machine data ─────────────────────────────────────────────
// GET /v1/machine
export const getAllMachines = async (params = {}) => {
  const { data } = await axios.get("machine", { params });
  return data;
};

// GET /v1/machine/maintenance-due
export const getMaintenanceDue = async (days = 7) => {
  const { data } = await axios.get("machine/maintenance-due", {
    params: { days },
  });
  return data;
};

// GET /v1/machine/:id/utilization
export const getMachineUtilization = async (id, startDate, endDate) => {
  const { data } = await axios.get(`machine/${id}/utilization`, {
    params: { start_date: startDate, end_date: endDate },
  });
  return data;
};

// ── Schedule data ────────────────────────────────────────────
// GET /v1/production/schedule/capacity
export const getCapacityUtilization = async (startDate, endDate, machineIds) => {
  const params = { start_date: startDate, end_date: endDate };
  if (machineIds) params.machine_ids = machineIds;
  const { data } = await axios.get("production/schedule/capacity", { params });
  return data;
};

// GET /v1/production/schedule/gantt
export const getGanttData = async (startDate, endDate, machineIds) => {
  const params = { start_date: startDate, end_date: endDate };
  if (machineIds) params.machine_ids = machineIds;
  const { data } = await axios.get("production/schedule/gantt", { params });
  return data;
};

// GET /v1/production/schedule/jobs/:stageCode
export const getJobsByStage = async (stageCode, params = {}) => {
  const { data } = await axios.get(
    `production/schedule/jobs/${stageCode}`,
    { params }
  );
  return data;
};

// ── Flow data ────────────────────────────────────────────────
// GET /v1/production/flow/default
export const getDefaultFlow = async () => {
  const { data } = await axios.get("production/flow/default");
  return data;
};

// ══════════════════════════════════════════════════════════════
// Composite / convenience fetchers used by dashboard widgets
// ══════════════════════════════════════════════════════════════

/**
 * Fetch everything the Production Dashboard needs in parallel.
 * Returns { productionReport, jobCompletionReport, machines, maintenanceDue, capacityData, flow }
 */
export const fetchProductionDashboard = async () => {
  const todayStr = today();
  const thirtyAgo = daysAgo(30);

  const [
    productionReport,
    jobCompletionReport,
    machines,
    maintenanceDue,
    capacityData,
    flow,
  ] = await Promise.allSettled([
    getProductionReport({ from_date: thirtyAgo, to_date: todayStr }),
    getJobCompletionReport({ from_date: thirtyAgo, to_date: todayStr }),
    getAllMachines(),
    getMaintenanceDue(7),
    getCapacityUtilization(thirtyAgo, todayStr),
    getDefaultFlow(),
  ]);

  return {
    productionReport: productionReport.status === "fulfilled" ? productionReport.value : null,
    jobCompletionReport: jobCompletionReport.status === "fulfilled" ? jobCompletionReport.value : null,
    machines: machines.status === "fulfilled" ? machines.value : [],
    maintenanceDue: maintenanceDue.status === "fulfilled" ? maintenanceDue.value : [],
    capacityData: capacityData.status === "fulfilled" ? capacityData.value : null,
    flow: flow.status === "fulfilled" ? flow.value : null,
  };
};

/**
 * Fetch stage-by-stage job counts for the horizontal bar chart.
 * Uses the default flow's stages and queries each stageCode.
 */
export const fetchStageJobCounts = async (stages) => {
  const results = await Promise.allSettled(
    stages.map((s) => getJobsByStage(s.code))
  );

  return stages.map((stage, i) => {
    const res = results[i];
    if (res.status !== "fulfilled") {
      return { stage_name: stage.name, code: stage.code, color: stage.color, total: 0, jobs: [] };
    }
    const val = res.value;
    return {
      stage_name: stage.name,
      code: stage.code,
      color: stage.color,
      total: val.total || 0,
      jobs: val.jobs || [],
    };
  });
};

/**
 * Fetch today's schedule from Gantt endpoint.
 */
export const fetchTodaySchedule = async () => {
  const todayStr = today();
  const data = await getGanttData(todayStr, todayStr);
  // Flatten all schedules across machines
  const schedules = (data.machines || []).flatMap((m) =>
    (m.schedules || []).map((s) => ({
      ...s,
      machine_name: m.machine_name,
      machine_code: m.machine_code,
    }))
  );
  return schedules;
};

/**
 * Fetch everything the Business Dashboard needs.
 */
export const fetchBusinessDashboard = async (fromDate, toDate) => {
  const [orderPipeline, customerSales] = await Promise.allSettled([
    getOrderPipelineReport(),
    getCustomerSalesReport({ from_date: fromDate, to_date: toDate }),
  ]);

  return {
    orderPipeline: orderPipeline.status === "fulfilled" ? orderPipeline.value : null,
    customerSales: customerSales.status === "fulfilled" ? customerSales.value : null,
  };
};

export default {
  // Reports
  getProductionReport,
  getJobCompletionReport,
  getWastageReport,
  getMaterialConsumptionReport,
  getOrderPipelineReport,
  getCustomerSalesReport,
  // Machine
  getAllMachines,
  getMaintenanceDue,
  getMachineUtilization,
  // Schedule
  getCapacityUtilization,
  getGanttData,
  getJobsByStage,
  // Flow
  getDefaultFlow,
  // Composites
  fetchProductionDashboard,
  fetchStageJobCounts,
  fetchTodaySchedule,
  fetchBusinessDashboard,
};
